import React from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRealtime } from "@/context/RealtimeContext";
import { fileUrl } from "@/lib/api";
import { timeAgo } from "@/lib/timeago";

export function NotificationBell() {
  const navigate = useNavigate();
  const { notifications = [], unreadNotifs = 0, markNotifRead, markAllRead } = useRealtime();
  const [open, setOpen] = React.useState(false);

  const onClick = (n) => {
    if (!n.read && markNotifRead) markNotifRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground"
          aria-label="Notifications"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadNotifs > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-[680] text-primary-foreground"
              data-testid="notification-unread-badge"
            >
              {unreadNotifs > 9 ? "9+" : unreadNotifs}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="text-sm font-[640] text-foreground">Notifications</span>
          {unreadNotifs > 0 && (
            <button onClick={markAllRead} className="text-xs font-[560] text-primary hover:underline" data-testid="notif-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto overscroll-contain" data-testid="notification-scroll">
          {notifications.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="notif-empty">You're all caught up.</div>
          ) : (
            <ul className="divide-y divide-border">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => onClick(n)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60 ${!n.read ? "bg-teal-50/50" : ""}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      {n.actor && n.actor.headshot_url && <AvatarImage src={fileUrl(n.actor.headshot_url)} alt="" />}
                      <AvatarFallback className="bg-teal-50 text-xs text-teal-700">{(n.actor && n.actor.display_name ? n.actor.display_name[0] : "S")}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm leading-snug text-foreground">{n.text}</p>
                      {n.type === "resource_share" && n.link && (
                        <span
                          onClick={(e) => { e.stopPropagation(); onClick(n); }}
                          className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-[600] text-primary-foreground"
                          data-testid={`notif-view-resource-${n.id}`}
                        >
                          View resource
                        </span>
                      )}
                      <p className="mt-0.5 text-xs text-muted-foreground">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
